import { useCallback } from "react";
import { useGetRequest } from "../ApiWrappers/useGetRequest";
import { Config } from "src/Utils/ConfigService/LoadConfig";
import { v1 } from "src/Utils/ApiVersions";

/**
 * What the clinic will be charged if it re-subscribes.
 *
 * Two lines: arrears for the grace days the clinic had access to but never paid for after a
 * cancellation, and the first period of the new subscription. Amounts are in minor units (cents),
 * as the licensing service and the payment provider both quote them, so divide before display.
 *
 * Both figures come from the same code that builds the checkout session, so what is shown here is
 * what gets billed. Tax is not included: the provider computes it at checkout against the
 * customer's billing address, so anything shown before then would be a guess.
 */
export interface IResubscribeInfo {
    hasOutstandingPayment: boolean;
    outstandingAmount: number;
    outstandingDescription: string;
    outstandingAccessFrom: string | null;
    outstandingAccessTo: string | null;
    subscriptionAmount: number;
    subscriptionDescription: string;
    subscriptionInterval: string;
    currency: string;
    totalAmount: number;
}

export const useGetResubscribeInfo = () => {
    const scope = Config.GetConfigData().auraLicensingService?.scopes[0];
    const baseUrl = Config.GetConfigData().auraLicensingService?.serviceUrl;
    const [getDetails, data, loading, error] = useGetRequest<IResubscribeInfo>();

    const getResubscribeInfo = useCallback((clinicId: string, priceId: string) => {
        if (clinicId && priceId) {
            const requestUrl = `${baseUrl}api/${v1}/payment/clinic/${clinicId}/resubscribe/info?priceId=${priceId}`;
            getDetails(scope, requestUrl);
        }
    }, [getDetails, scope, baseUrl]);

    return {
        getResubscribeInfo,
        data,
        loading,
        error
    };
};
